import { useLazyQuery, useQuery } from '@apollo/client';

import {
  CALENDLY_EVENT_ID,
  CURRENT_USER,
  INDICATORS,
  LOCATIONS,
  MEDICAL_HISTORY_PDF,
  NOTES,
  PRACTITIONER_BY_PRENUVO_ID,
  PRACTITIONERS_BY_PRENUVO_IDS,
  PRESCRIPTION_PDF,
  PRESCRIPTIONS,
  SAFETY_QUESTIONS,
  SEARCH_USER_IDS,
  SEARCH_USERS,
  STUDIES,
  STUDY,
} from '../../graphql/queries';
import { StudyFilterInput } from '../../types';

// PII
// ----------------------------------------------------------------------

export const useCurrentUserQuery = () => {
  const { data, error, loading } = useQuery(CURRENT_USER, {
    context: {
      clientName: 'pii',
    },
  });

  return { data: data && data.currentUser, error, loading };
};

export const useUsersQuery = () => {
  const [fetchUsers, { data, error, loading }] = useLazyQuery(SEARCH_USERS, {
    context: {
      clientName: 'pii',
    },
  });
  return { fetchUsers, data: data && data.searchUsers, error, loading };
};
export const useUserIdsQuery = () => {
  const [fetchUsers, { data, error, loading }] = useLazyQuery(SEARCH_USER_IDS, {
    context: {
      clientName: 'pii',
    },
  });
  return { fetchUsers, data: data && data.searchUsers, error, loading };
};

export const useNPsQuery = () => {
  const { data, error, loading } = useQuery(SEARCH_USERS, {
    context: {
      clientName: 'pii',
    },
    variables: {
      userFilter: {
        roles: 'np',
      },
    },
  });
  return { data: data && data.searchUsers, error, loading };
};

// APPS
// ----------------------------------------------------------------------
export const useCalendlyEventQuery = (eventId: string) => {
  const [fetchCalendlyEvent, { data, error, loading }] = useLazyQuery(CALENDLY_EVENT_ID, {
    fetchPolicy: 'network-only',
    context: {
      clientName: 'apps',
    },
    variables: {
      eventId,
    },
  });
  return { fetchCalendlyEvent, data: data && data.calendlyEventDetails, error, loading };
};

export const useLocationsQuery = () => {
  const { data, error, loading } = useQuery(LOCATIONS, {
    context: {
      clientName: 'apps',
    },
  });
  return { data: data && data.locations, error, loading };
};


export const useIndicatorsQuery = (studyFilter: StudyFilterInput) => {
  const [fetchStudies, { data, error, loading }] = useLazyQuery(INDICATORS, {
    fetchPolicy: 'network-only',
    context: {
      clientName: 'apps',
    },
    variables: {
      studyFilter,
    },
  });
  return { fetchStudies, data: data && data.npdStudies, error, loading };
};
export const useStudiesQuery = (studyFilter: StudyFilterInput) => {
  const [fetchStudies, { data, error, loading }] = useLazyQuery(STUDIES, {
    fetchPolicy: 'network-only',
    context: {
      clientName: 'apps',
    },
    variables: {
      studyFilter,
    },
  });
  return { fetchStudies, data: data && data.npdStudies, error, loading };
};

export const useStudyQuery = (studyID: string) => {
  const [fetchStudy, { data, error, loading }] = useLazyQuery(STUDY, {
    context: {
      clientName: 'apps',
    },
    variables: {
      studyID,
    },
  });
  return { fetchStudy, data: data && data.npdStudies, error, loading };
};

// HI
// ----------------------------------------------------------------------
export const useSafetyQuestionsQuery = (studyIds: Array<string>) => {
  const [fetchSafetyQuestions, { data, error, loading }] = useLazyQuery(SAFETY_QUESTIONS, {
    context: {
      clientName: 'hi',
    },
    variables: {
      studyIds,
      prescription: true,
    },
  });
  return {
    fetchSafetyQuestions,
    data: data && data.safetyQuestions && data.safetyQuestions.state,
    error,
    loading,
  };
};

export const usePrescriptionsQuery = (patientId: string, studyId?: string) => {
  const [fetchPrescriptions, { data, error, loading }] = useLazyQuery(PRESCRIPTIONS, {
    variables: {
      patientId,
      studyId,
    },
    context: {
      clientName: 'hi',
    },
  });

  return { fetchPrescriptions, data: data && data.prescriptions, error, loading };
};

export const useNotesQuery = (studyId: string) => {
  const [fetchNotes, { data, error, loading }] = useLazyQuery(NOTES, {
    variables: {
      studyId,
      noteType: 'np',
    },
    context: {
      clientName: 'hi',
    },
  });

  return { fetchNotes, data: data && data.studyHealthPractitionerNotes, error, loading };
};

export const useGetMedicalHistoryPDF = (studyId: string) => {
  const { data, error, loading } = useQuery(MEDICAL_HISTORY_PDF, {
    variables: {
      studyId: studyId,
    },
    context: {
      clientName: 'hi',
    },
  });
  return { data, error, loading };
};

// HPD
// ----------------------------------------------------------------------
export const useGetPractitionerByPrenuvoId = (prenuvoId: string) => {
  const [getPractitionerByPrenuvoId, { data, error, loading }] = useLazyQuery(
    PRACTITIONER_BY_PRENUVO_ID,
    {
      context: {
        clientName: 'hpd',
      },
      variables: {
        id: prenuvoId,
      },
    },
  );
  return {
    getPractitionerByPrenuvoId,
    data,
    error,
    loading,
  };
};

export const useGetPractitionersByPrenuvoIds = (prenuvoIds: string[]) => {
  const [getPractitionersByPrenuvoIds, { data, error, loading }] = useLazyQuery(
    PRACTITIONERS_BY_PRENUVO_IDS,
    {
      context: {
        clientName: 'hpd',
      },
      variables: {
        practitionersList: prenuvoIds,
      },
    },
  );
  return {
    getPractitionersByPrenuvoIds,
    data,
    error,
    loading,
  };
};

export const usePrescriptionPDFQuery = (studyId: string) => {
  const [fetchPrescriptionPDF, { data, error, loading }] = useLazyQuery(PRESCRIPTION_PDF, {
    variables: {
      studyId,
    },
    context: {
      clientName: 'apps',
    },
    fetchPolicy: 'no-cache',
  });
  return {
    fetchPrescriptionPDF,
    data: data && data.prescription && data.prescription.pdf,
    error,
    loading,
  };
};
